import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);

    // Small delay to let iOS autofill complete
    await new Promise(resolve => setTimeout(resolve, 100));

    // Fix for iOS autofill - read values directly from DOM
    const emailValue = emailRef.current?.value || formData.email || '';
    const passwordValue = passwordRef.current?.value || formData.password || '';

    console.log('Login attempt:', { email: emailValue, passLength: passwordValue.length });

    if (!emailValue || !passwordValue) {
      toast.error('Please enter username and password');
      setLoading(false);
      return;
    }

    try {
      await login(emailValue.trim(), passwordValue);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24">
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-12"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Website
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-8">
              <img
                src="/logo-mark-red.png"
                alt="Someones Direction"
                className="h-10 w-auto"
              />
              <span className="font-body text-sm tracking-[0.3em] uppercase text-foreground font-semibold">
                Someones&nbsp;Direction
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mt-8 mb-3">
              {isLogin ? 'Willkommen zurück' : 'Konto erstellen'}
            </h1>
            <p className="text-muted-foreground">
              {isLogin
                ? 'Zugangsdaten eingeben, um das Portal zu öffnen'
                : 'Konto erstellen, um Inhalte zu verwalten'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="bg-card border-border text-foreground placeholder:text-muted-foreground"
                  required={!isLogin}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Benutzername</Label>
              <Input
                ref={emailRef}
                id="email"
                type="text"
                name="username"
                autoComplete="username"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Benutzername"
                className="bg-card border-border text-foreground placeholder:text-muted-foreground"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Passwort</Label>
              <div className="relative">
                <Input
                  ref={passwordRef}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-card border-border text-foreground placeholder:text-muted-foreground pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (
                isLogin ? 'Anmelden' : 'Konto erstellen'
              )}
            </Button>
          </form>

        </motion.div>
      </div>

      {/* Right side - rote Markenfläche */}
      <div className="hidden lg:block lg:w-1/2 relative bg-brand overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center px-12"
          >
            <img
              src="/logo-mark-white.png"
              alt=""
              className="w-56 h-auto mx-auto mb-10"
            />
            <h2 className="font-display text-4xl text-white mb-4">
              Kundenportal
            </h2>
            <p className="text-white/70 max-w-md">
              Inhalte, Portfolio, Anfragen und mehr an einem Ort.
            </p>
          </motion.div>
        </div>
        <span className="absolute bottom-8 left-0 right-0 text-center font-accent text-white/80 text-2xl">
          Clear direction. Strong work.
        </span>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-yellow" />
      </div>
    </div>
  );
}
